import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { CancellationReason } from '@/entities/consignments';
import { getCancelationReasons } from '../model';

interface CancelConsignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: CancellationReason, remarks: string) => void;
}

export function CancelConsignmentDialog({ open, onOpenChange, onConfirm }: CancelConsignmentDialogProps) {
  const { t } = useTranslation();

  const [reason, setReason] = useState<CancellationReason>('A');
  const [remarks, setRemarks] = useState('');

  const CANCELLATION_REASONS = getCancelationReasons(t);

  const handleSubmit = () => {
    onConfirm(reason, remarks);
    setReason('A');
    setRemarks('');
    onOpenChange(false);
  };

  const handleClose = () => {
    setReason('A');
    setRemarks('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('newcmr.reqstatus.CA.title.message')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">{t('newcmr.reqstatus.CA.cancellationReason.label')}</Label>
            <Select value={reason} onValueChange={(event) => setReason(event as CancellationReason)}>
              <SelectTrigger id="reason">
                <SelectValue placeholder={t('newcmr.reqstatus.CA.cancellationReason.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="remarks">{t('newcmr.reqstatus.CA.comment.label')}</Label>
            <Textarea
              id="remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={t('newcmr.reqstatus.CA.comment.placeholder')}
              className="min-h-32"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t('newcmr.reqstatus.CA.close.button')}
          </Button>
          <Button onClick={handleSubmit} className="min-w-24">
            {t('newcmr.reqstatus.CA.save.button')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
